import React, { useContext, useEffect, useRef, useState } from 'react';
import { useLazyQuery } from '@apollo/client';
//import { parseRawData } from '../../parsers';
import { UserContext } from 'contexts/ConfigContext';
import { getConfig } from 'utils/helperMethods';
import _ from 'lodash';
import {
    getAttributionFeatureDimensionLevel,
    getSalesAttribute,
    getSalesAttributionFeatureLevelData,
} from 'gqlQueries/growthAnalysisQueries';
import {
    salesAtrributionCompetitorBreakdownParser,
    salesAttributionFeatureLevelParser,
} from 'parser/salesAttributionDataParser';

const errorMessage: string = 'Something went wrong!';

function useFetchAttributionFeatureDimensionLevel() {
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<null | string>(null);
    const [data, setData] = useState<any | null>(null);
    const [count, setCount] = useState(0);
    const [isBreakdown, setIsBreakdown] = useState(false);

    const { appConfig } = useContext(UserContext);
    const growthAnalysisConfig = getConfig('growthAnalysis', appConfig);
    const salesAttributionChart2DisabledItems = growthAnalysisConfig?.salesAttributionChart2DisabledItems;

    const breakdownFeatureSpec = growthAnalysisConfig.breakdownFeatureSpec;

    const shouldAppendDataRef = useRef(false);

    const [
        fetchAttributeSales,
        { data: attributeSalesData, loading: attributeSalesLoading, error: attributeSalesError },
    ] = useLazyQuery(getAttributionFeatureDimensionLevel, {
        onCompleted: onFetchComplete,
        onError: onFetchError,
    });

    //called when fetching is complete
    function onFetchComplete(payload: any) {
        try {
            const { getSalesAttributionFeatureDimensionLevel: featureGroup } = payload;
            const isBreakDownChart = breakdownFeatureSpec.includes(featureGroup?.featureName);
            //const featureGroup = payload;
            //if data present parse it
            if (featureGroup && 'featureDimensionLevelData' in featureGroup) {
                if (featureGroup?.count === 0) throw 'No Data';
                let parsedData: any = null;
                if (breakdownFeatureSpec.includes(featureGroup?.featureName)) {
                    parsedData = salesAtrributionCompetitorBreakdownParser(featureGroup?.featureDimensionLevelData);
                    setIsBreakdown(true);
                } else {
                    parsedData = salesAttributionFeatureLevelParser(
                        featureGroup?.featureDimensionLevelData,
                        'attributeValue',
                        'attributeName',
                        false,
                    );
                    setIsBreakdown(false);
                }

                //if data present already then request coming from infinite scroll
                if (shouldAppendDataRef.current && data && 'data' in data && parsedData) {
                    parsedData.minVal.abs = Math.min(parsedData.minVal.abs, data.minVal.abs);
                    parsedData.minVal.percentage = Math.min(parsedData.minVal.percentage, data.minVal.percentage);
                    parsedData.maxVal.abs = Math.max(parsedData.maxVal.abs, data.maxVal.abs);
                    parsedData.maxVal.percentage = Math.max(parsedData.maxVal.percentage, data.maxVal.percentage);
                    parsedData.data.abs = [...data?.data?.abs, ...parsedData.data?.abs];
                    parsedData.data.percentage = [...data?.data?.percentage, ...parsedData.data?.percentage];
                    parsedData.maxCharacters = Math.max(parsedData.maxCharacters, data.maxCharacters);

                    if (isBreakDownChart && data.breakdown && parsedData.breakdown) {
                        parsedData.breakdown.abs = { ...data.breakdown.abs, ...parsedData.breakdown.abs };
                        parsedData.breakdown.percentage = {
                            ...data.breakdown.percentage,
                            ...parsedData.breakdown.percentage,
                        };
                    }
                }
                setCount(featureGroup?.count || 0);
                setData(parsedData);

                return;
            }
            setData(null);
        } catch (error) {
            console.error(errorMessage, error);
            onFetchError(error);
        } finally {
            setLoading(false);
        }
    }

    //called when API throws an error
    function onFetchError(error: any = errorMessage) {
        //sending empty data to error to avoid null data being sent
        setData(null);
        setCount(0);
        setLoading(false);
        setError(error);
    }

    return {
        loading,
        error,
        data,
        count,
        isBreakdown,
        fetchAttributeSales: (variables: any) => {
            setLoading(true);
            setError(null);
            shouldAppendDataRef.current = !!variables.variables?.offset;
            if (!variables.variables?.offset) setData(null);
            //onFetchComplete(dummyData);
            fetchAttributeSales(variables);
        },
    };
}

export { useFetchAttributionFeatureDimensionLevel };
